import React, { useMemo } from 'react';
import { BsFillCheckCircleFill, BsFillXCircleFill, BsTrash } from 'react-icons/bs';
import { Box, Button, HStack, Icon, IconButton, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../components/Spinner';

const VerifyDomainHeader = ({
  isEnabled,
  domainName,
  domainStatus,
  handleVerifyDomain,
  handleDisableSetting,
  handleDeleteDomain,
  domainVerifying,
  domainDeleting,
  domainDetailsLoading
}) => {
  const domainVerified = useMemo(() => domainStatus === 'active', [domainStatus]);

  return (
    <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
      <HStack justifyContent="space-between">
        <Text mb={4} textStyle="bodyLg500">Verify Your Domain with PINS</Text>
        <IconButton
          aria-label="delete-custom-domain-button"
          icon={<Icon as={BsTrash} />}
          isLoading={domainDeleting}
          size="sm"
          variant="accentError"
          onClick={handleDeleteDomain}
        />
      </HStack>

      <HStack justifyContent="space-between">
        <Box pr={12} textAlign="left">
          <HStack alignItems="center" spacing={2}>
            <Text textStyle="bodyMd500">Domain:</Text>
            {domainName
              ? (<Text color="greyScale.700" textStyle="bodyMd500">{domainName}</Text>)
              : (<Box pl={2}><LoadingSpinner isInline spinnerSize="sm" /></Box>)}
          </HStack>

          <HStack spacing={2}>
            <Text textStyle="bodyMd500">Domain Status:</Text>

            {domainStatus
              ? (
                <>
                  <Icon
                    as={domainVerified ? BsFillCheckCircleFill : BsFillXCircleFill}
                    color={domainVerified ? 'success.primary.600' : 'error.primary.600'}
                  />
                  <Text color="greyScale.700" textStyle="bodyMd500">{domainVerified ? 'Verified' : 'Unverified'}</Text>
                </>
              )
              : (<Box pl={2}><LoadingSpinner isInline spinnerSize="sm" /></Box>)}
          </HStack>

          <HStack mb={4} spacing={2}>
            <Text textStyle="bodyMd500">Setting:</Text>
            <Icon
              as={isEnabled ? BsFillCheckCircleFill : BsFillXCircleFill}
              color={isEnabled ? 'success.primary.600' : 'error.primary.600'}
            />
            <Text color="greyScale.700" textStyle="bodyMd500">
              {isEnabled ? 'On' : 'Off'}
            </Text>
          </HStack>

          <Text color="greyScale.700" mb={2} textStyle="bodySm">
            Please add the following SPF and DKIM TXT records to your domain&apos;s zone file.
          </Text>

          <Text color="greyScale.700" mb={2} textStyle="bodySm">
            Need help?
            {' '}
            <a
              href="https://help.mailgun.com/hc/en-us/articles/360026833053#:~:text=Step%202%3A%20Add%20DNS%20Records%20to%20Hosting%20Provider"
              rel="noreferrer"
              target="_blank"
            >
              Click here for a guide.
            </a>
          </Text>
        </Box>
        <Box>
          <Button
            isLoading={domainDeleting || domainVerifying || domainDetailsLoading}
            variant="primary"
            onClick={isEnabled ? handleDisableSetting : handleVerifyDomain}
          >
            {isEnabled ? 'Disable Setting' : 'Verify and Activate Setting'}
          </Button>
        </Box>
      </HStack>
    </Box>
  );
};

VerifyDomainHeader.propTypes = {
  isEnabled: PropTypes.bool,
  domainName: PropTypes.string,
  domainStatus: PropTypes.string,
  handleVerifyDomain: PropTypes.func,
  handleDisableSetting: PropTypes.func,
  handleDeleteDomain: PropTypes.func,
  domainVerifying: PropTypes.bool,
  domainDeleting: PropTypes.bool,
  domainDetailsLoading: PropTypes.bool
};

export default VerifyDomainHeader;
